/**
 * Single source of truth for authenticated top-nav menu pages.
 * React-free so Playwright E2E can import the same catalog.
 */

/** PAGE permission slug → frontend path (mirrors page-routes PAGE_ROUTES). */
export const NAV_PAGE_ROUTES: Record<string, string> = {
  dashboard: "/dashboard",
  "identity-hub": "/iam",
  opportunities: "/opportunities",
  "persona-opportunities": "/persona-opportunities",
  workspace: "/workspace",
  admin: "/admin",
  personas: "/personas",
  "pre-flight": "/pre-flight",
  "post-flight": "/post-flight",
  "pre-flight-scripts": "/pre-flight-scripts",
  marketplace: "/marketplace",
  "marketplace.admin": "/marketplace/admin",
  "marketplace.orders": "/marketplace/orders",
  "marketplace.partner": "/marketplace/partner",
  overview: "/overview",
  drilldown: "/drilldown",
  prompts: "/prompts",
  "public-api": "/api-tokens",
  "script-delivery": "/script-delivery",
};

export type NavGroupDef = {
  slug: string;
  labelKey: string;
  label: string;
  pages: string[];
};

/**
 * Dashboard is the primary header link. It stays in Explore so the catalog is
 * complete; TopNav renders it as a top-level link and omits it from the dropdown.
 */
export const PRIMARY_NAV_SLUG = "dashboard";

/**
 * Top-nav mega-menu groups. Order matches the live app.
 */
export const NAV_GROUPS: NavGroupDef[] = [
  {
    slug: "make",
    labelKey: "nav.make",
    label: "Make",
    pages: [
      "opportunities",
      "persona-opportunities",
      "personas",
      "pre-flight",
      "post-flight",
      "pre-flight-scripts",
      "marketplace",
    ],
  },
  {
    slug: "explore",
    labelKey: "nav.explore",
    label: "Explore",
    pages: ["dashboard", "overview", "drilldown", "workspace"],
  },
  {
    slug: "manage",
    labelKey: "nav.manage",
    label: "Manage",
    pages: [
      "admin",
      "identity-hub",
      "prompts",
      "public-api",
      "marketplace.admin",
      "marketplace.orders",
      "script-delivery",
    ],
  },
];

/** Pages that require the privileged E2E identity (SUPER_TENANT / manual grants). */
export const PRIVILEGED_NAV_PAGE_SLUGS = [
  "admin",
  "prompts",
  "marketplace.admin",
  "marketplace.orders",
  "public-api",
  "script-delivery",
] as const;

const PRIVILEGED_SET = new Set<string>(PRIVILEGED_NAV_PAGE_SLUGS);

const SUPER_TENANT_ONLY_NAV_SLUGS = new Set<string>(["script-delivery"]);

/** Script delivery is an Adge-admin page. A grant on another tenant does not show it. */
export function isNavSlugVisibleForTenantType(slug: string, tenantType: string | undefined): boolean {
  if (!SUPER_TENANT_ONLY_NAV_SLUGS.has(slug)) return true;
  return tenantType === "SUPER_TENANT";
}

/**
 * Neither smoke identity is seeded to open these menu pages.
 * Post-Flight starts ungranted, so it stays out of both smoke lists.
 */
const SMOKE_EXCLUDED_NAV_PAGE_SLUGS = ["post-flight"] as const;

const SMOKE_EXCLUDED_SET = new Set<string>(SMOKE_EXCLUDED_NAV_PAGE_SLUGS);

/** Every menu PAGE slug from NAV_GROUPS, unique, catalog order. */
export function allNavPageSlugs(): string[] {
  const slugs: string[] = [];
  const seen = new Set<string>();
  for (const group of NAV_GROUPS) {
    for (const page of group.pages) {
      if (seen.has(page)) continue;
      seen.add(page);
      slugs.push(page);
    }
  }
  return slugs;
}

/** Standard (non-privileged) menu pages the default E2E Automation tenant should see. */
export function standardNavPageSlugs(): string[] {
  return allNavPageSlugs().filter(
    (slug) => !PRIVILEGED_SET.has(slug) && !SMOKE_EXCLUDED_SET.has(slug),
  );
}

export function navPageRoute(slug: string): string | null {
  return NAV_PAGE_ROUTES[slug] ?? null;
}

export type NavSmokePage = {
  slug: string;
  route: string;
};

/** Standard menu pages with resolved routes for remote smoke. */
export function standardNavSmokePages(): NavSmokePage[] {
  return standardNavPageSlugs().map((slug) => {
    const route = navPageRoute(slug);
    if (!route) throw new Error(`Missing route for standard nav slug: ${slug}`);
    return { slug, route };
  });
}

/** Privileged menu pages with resolved routes for remote smoke. */
export function privilegedNavSmokePages(): NavSmokePage[] {
  return PRIVILEGED_NAV_PAGE_SLUGS.map((slug) => {
    const route = navPageRoute(slug);
    if (!route) throw new Error(`Missing route for privileged nav slug: ${slug}`);
    return { slug, route };
  });
}
